/**
 * Date / time helpers for payload serialisation.
 *
 * The backend expects:
 *  - `start_date` / `end_date` in DD-MM-YYYY format
 *  - `time` in HH:MM 24-hour format
 *  - Both values in UTC (not the user's local timezone)
 *
 * HTML `<input type="date">` returns YYYY-MM-DD and `<input type="time">`
 * returns HH:MM, both interpreted as the user's local time. These helpers
 * combine them, convert to UTC, and format them the way the API wants.
 */

/**
 * Combine a local date (YYYY-MM-DD) + local time (HH:MM) into a UTC
 * date / time pair.
 *
 * @param {string} localDateIso  "2026-04-09"
 * @param {string} localTimeHhMm "06:40"
 * @returns {{ date: string, time: string }} e.g. `{ date: "09-04-2026", time: "01:10" }`
 */
export function toUtcDateTime(localDateIso, localTimeHhMm) {
  if (!localDateIso) return { date: "", time: "" };

  const [y, m, d] = localDateIso.split("-").map(Number);
  let hh = 0;
  let mm = 0;
  if (localTimeHhMm) {
    const parts = localTimeHhMm.split(":").map(Number);
    hh = Number.isFinite(parts[0]) ? parts[0] : 0;
    mm = Number.isFinite(parts[1]) ? parts[1] : 0;
  }

  // Build a Date object interpreted as local time, then read its UTC parts.
  // This correctly shifts the date across midnight when the timezone offset
  // pushes the UTC time into the previous or next day.
  const local = new Date(y || 1970, (m || 1) - 1, d || 1, hh, mm, 0, 0);

  const pad = (n) => String(n).padStart(2, "0");

  return {
    date: `${pad(local.getUTCDate())}-${pad(local.getUTCMonth() + 1)}-${local.getUTCFullYear()}`,
    time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
  };
}

/**
 * Convert a list of selected weekday keys into a 7-element 0/1 array.
 *
 * The order follows ISO-like Mon → Sun (Monday at index 0, Sunday at index 6),
 * which matches the WEEKDAYS list in NewTriggerModal.
 *
 * @param {string[]} selectedDays e.g. `["Mon", "Wed"]`
 * @returns {number[]} e.g. `[1,0,1,0,0,0,0]`
 */
export function toWeekdaysArray(selectedDays) {
  const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const set = new Set(selectedDays || []);
  return order.map((day) => (set.has(day) ? 1 : 0));
}

/**
 * Inverse of {@link toWeekdaysArray} — turns a 7-element 0/1 array back
 * into the Mon..Sun key list the modal renders. Used when populating the
 * Edit Trigger modal with an existing schedule.
 */
export function fromWeekdaysArray(arr) {
  const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  if (!Array.isArray(arr)) return [];
  return order.filter((_, idx) => !!arr[idx]);
}

/**
 * Inverse of {@link toUtcDateTime} — turns a backend-shaped
 * `DD-MM-YYYY` date + `HH:MM(:SS)` UTC time into a local
 * `YYYY-MM-DD` date + `HH:MM` time pair the native
 * `<input type="date">` / `<input type="time">` controls accept.
 *
 * Used when populating the Edit Trigger modal — the schedule was stored
 * in UTC by {@link toUtcDateTime}; we need to re-render it in the
 * user's current timezone so the values shown match what they originally
 * picked.
 *
 * @param {string} utcDate  "09-04-2026"
 * @param {string} utcTime  "01:10" or "01:10:00"
 * @returns {{ date: string, time: string }} e.g. `{ date: "2026-04-09", time: "06:40" }`
 */
export function fromUtcDateTime(utcDate, utcTime) {
  if (!utcDate) return { date: "", time: "" };

  const parts = String(utcDate).split("-").map(Number);
  let d = 0;
  let m = 0;
  let y = 0;
  // DD-MM-YYYY is the canonical backend shape. Tolerate YYYY-MM-DD too
  // so a call-site that already has an ISO date doesn't trip up on a
  // future refactor.
  if (parts.length === 3) {
    if (parts[0] > 31) {
      // YYYY-MM-DD
      [y, m, d] = parts;
    } else {
      [d, m, y] = parts;
    }
  }
  const tParts = String(utcTime || "").split(":").map(Number);
  const hh = Number.isFinite(tParts[0]) ? tParts[0] : 0;
  const mm = Number.isFinite(tParts[1]) ? tParts[1] : 0;

  // Construct the UTC instant, then read back local parts so the value
  // in the input matches what the user will see rendered elsewhere.
  const utc = new Date(Date.UTC(y || 1970, (m || 1) - 1, d || 1, hh, mm, 0));
  if (Number.isNaN(utc.getTime())) return { date: "", time: "" };

  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${utc.getFullYear()}-${pad(utc.getMonth() + 1)}-${pad(utc.getDate())}`,
    time: `${pad(utc.getHours())}:${pad(utc.getMinutes())}`,
  };
}

/**
 * Parse a timestamp in ISO form or "YYYY-MM-DD HH:MM:SS" (backend shape).
 * The backend-shape strings are treated as UTC since that's what the API
 * returns. Returns `null` if the value can't be parsed.
 */
function parseTimestamp(raw) {
  return parseUtcTimestamp(raw);
}

/**
 * Short "time since" label with a month-day fallback past one week.
 *
 * Variant of {@link timeAgo} for surfaces that show ages in arbitrarily
 * old data — e.g. the KC stats row, where an item recalled six months
 * ago shouldn't render as "183d ago" (forces mental arithmetic). At
 * one week the label switches to a localized "Apr 12" string so the
 * user gets an anchor date.
 *
 * Accepts an optional ``now`` so tests are deterministic regardless
 * of the developer's clock — the regular ``timeAgo`` doesn't need
 * this because its consumers are all live recent data.
 *
 * @param {string|Date|null} isoTimestamp ISO string or Date.
 * @param {Date} [now=new Date()]         Reference instant.
 * @returns {string|null}
 */
export function formatRelativeTime(isoTimestamp, now = new Date()) {
  if (!isoTimestamp) return null;
  const then = isoTimestamp instanceof Date
    ? isoTimestamp
    : new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) return null;
  const deltaMs = now.getTime() - then.getTime();
  if (deltaMs < 0) return "just now";
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  // Older than a week — show a month-day so the user sees an anchor
  // date instead of an unbounded "Nd ago" they can't intuit.
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Short "time since" label: "just now", "4m ago", "2h ago", "3d ago".
 */
export function timeAgo(raw) {
  const past = parseTimestamp(raw);
  if (!past) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - past.getTime()) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

/**
 * Elapsed label for an in-progress run — same bucketing as `timeAgo`
 * but without the "ago" suffix: "just now", "4m", "2h", "3d".
 */
/**
 * Parse a backend timestamp as UTC and return a `Date` anchored to that
 * instant (displayed in the user's local timezone by any subsequent
 * `toLocale*` call). Accepts ISO strings, "YYYY-MM-DD HH:MM:SS", or a
 * `Date` instance.
 */
export function parseUtcTimestamp(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  const s = String(raw);
  // ISO string already carries timezone info — let the browser handle it.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Otherwise assume the backend shape "YYYY-MM-DD HH:MM:SS" is UTC.
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!m) {
    const fallback = new Date(s);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const [, y, mo, d, hh, mm, ss] = m;
  const utc = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    Number(ss || 0),
  );
  return new Date(utc);
}

/**
 * Format a UTC timestamp from the backend as a short local-time label:
 * "Today, 1:24 PM", "Tomorrow, 7:00 AM", or "Mar 12, 2:00 PM".
 * Returns `fallback` (default "On resume") when the value is missing.
 */
export function formatTriggerTime(raw, fallback = "On resume") {
  if (!raw) return fallback;
  const target = parseUtcTimestamp(raw);
  if (!target) return String(raw);

  const now = new Date();
  const startOfDay = (dt) =>
    new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const dayDiff = Math.round(
    (startOfDay(target) - startOfDay(now)) / 86400000,
  );

  const timeLabel = target.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (dayDiff === 0) return `Today, ${timeLabel}`;
  if (dayDiff === 1) return `Tomorrow, ${timeLabel}`;
  if (dayDiff === -1) return `Yesterday, ${timeLabel}`;
  const dateLabel = target.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(target.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${dateLabel}, ${timeLabel}`;
}

export function formatElapsed(raw) {
  const start = parseTimestamp(raw);
  if (!start) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  return `${Math.floor(diffHr / 24)}d`;
}

/**
 * Format a duration (ms between two instants) as a compact label for the
 * step-timings UI. Unlike {@link formatElapsed} this is TWO-level precision
 * and does NOT say "just now" — a genuine 3-second step should read "3s",
 * not be hidden behind a friendly label.
 *
 * Examples:
 *   0         → "0s"
 *   900       → "1s"     (sub-second rounded up — users shouldn't see "0s"
 *                         next to a step that visibly took a beat)
 *   3_500     → "4s"
 *   45_000    → "45s"
 *   65_000    → "1m 5s"
 *   599_000   → "9m 59s"
 *   600_000   → "10m"    (once the minute-floor hits double digits we drop
 *                         the seconds — the tail doesn't pay for itself)
 *   3_540_000 → "59m"
 *   3_600_000 → "1h 0m"
 *   7_260_000 → "2h 1m"
 *
 * The ms-level input is deliberate: callers pass `endMs - startMs` straight
 * from `Date.parse()` / `Date.now()`. When `endMs` is omitted we treat it
 * as "still running" and use `Date.now()` — the same live-tick behaviour
 * `formatElapsed` has, but with the richer hh:mm:ss output this UI needs.
 *
 * @param {number|string|Date|null|undefined} startMs
 *   Either a number (ms since epoch or a float unix time × 1000 already
 *   converted — in which case pass it directly), or anything
 *   `parseUtcTimestamp` accepts. ``null`` / ``undefined`` → ``""``.
 * @param {number|string|Date|null|undefined} [endMs]
 *   Same shape as ``startMs``. Omit / pass ``null`` to clock "still
 *   running" durations against the wall clock.
 * @returns {string} — compact duration label, or ``""`` when ``startMs``
 *   cannot be parsed.
 */
export function formatDuration(startMs, endMs) {
  const startT = toMs(startMs);
  if (startT == null) return "";
  const endT = endMs == null ? Date.now() : toMs(endMs);
  if (endT == null) return "";
  let diffMs = endT - startT;
  if (!Number.isFinite(diffMs) || diffMs < 0) diffMs = 0;

  // Sub-second floor — show "0s" for a genuine zero-duration record
  // (e.g. two events same tick) but round any partial second UP so a
  // user-visible beat never renders as "0s".
  if (diffMs === 0) return "0s";
  const totalSec = diffMs < 1000 ? 1 : Math.round(diffMs / 1000);

  if (totalSec < 60) return `${totalSec}s`;

  const totalMin = Math.floor(totalSec / 60);
  const remSec = totalSec % 60;

  if (totalMin < 10) {
    // Under 10 minutes — show the seconds tail. "1m 5s" is meaningfully
    // different from "1m 55s" in this range; the extra glyph pays for
    // itself.
    return remSec === 0 ? `${totalMin}m` : `${totalMin}m ${remSec}s`;
  }

  if (totalMin < 60) {
    // 10m–59m: drop the seconds. At this scale "12m 34s" is noise.
    return `${totalMin}m`;
  }

  const totalHr = Math.floor(totalMin / 60);
  const remMin = totalMin % 60;
  return `${totalHr}h ${remMin}m`;
}

/**
 * Best-effort coercion to a number of milliseconds since epoch. Accepts:
 *  • A finite number — returned as-is (assumed ms).
 *  • Anything `parseUtcTimestamp` accepts — parsed, then `.getTime()`.
 *  • `null` / `undefined` / unparseable → `null`.
 *
 * Private to this module; consumers pass their own shape and we normalise.
 */
function toMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  const parsed = parseUtcTimestamp(v);
  return parsed ? parsed.getTime() : null;
}

/**
 * Format a BE timestamp into a 12-hour LOCAL-TIME-only clock label
 * ("3:00 PM"). Date is intentionally omitted — every consumer pairs
 * this with a clock-icon pill that already conveys "this is a time",
 * so the date would be redundant noise on the card.
 *
 * Accepts the same shapes as ``toMs``:
 *  • ISO 8601 strings ("2026-06-03T09:30:00Z", "...+05:30")
 *  • epoch seconds (any number < 1e12)
 *  • epoch milliseconds (numbers ≥ 1e12)
 *  • "YYYY-MM-DD HH:MM:SS" backend shape (parsed as UTC)
 *  • a Date instance
 *
 * Composes "h:MM AM/PM" by hand rather than via
 * ``toLocaleTimeString`` so the shape is identical on every user's
 * machine — only the *value* shifts to their local TZ (locale-driven
 * formatters would produce "15.00" / "3:00 p. m." / "3:00 PM IST"
 * inconsistently). Hour is NOT zero-padded (12-hour convention);
 * minutes are zero-padded.
 *
 * Anything we can't parse returns the input via ``String(value)`` so
 * an unexpected non-time field still surfaces on the UI instead of
 * "Invalid Date".
 *
 * @param {string|number|Date|null|undefined} value
 * @returns {string} "h:MM AM/PM" or unchanged input on parse failure.
 */
/**
 * Normalise a timestamp value to a local ``Date``, or ``null`` when it
 * can't be parsed. Shared by the clock + message-age formatters so both
 * accept the same inputs (epoch seconds/ms, ISO strings, ``Date``).
 */
function toLocalDate(value) {
  if (value == null || value === "") return null;
  // For numeric inputs, use toMs's seconds-vs-ms heuristic so
  // BE-emitted epoch seconds (e.g. 1780466400) get scaled up
  // before Date parses them.
  let ms;
  if (typeof value === "number" && Number.isFinite(value)) {
    ms = value < 1e12 ? value * 1000 : value;
  } else {
    ms = toMs(value);
    if (ms == null) {
      // Last-ditch — accept formats parseUtcTimestamp rejected
      // (e.g. "2026-06-03 09:30 UTC") via the looser Date.parse.
      const parsed = Date.parse(String(value));
      if (!Number.isFinite(parsed)) return null;
      ms = parsed;
    }
  }
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 12-hour wall-clock time in the viewer's timezone, e.g. "2:20 PM". */
function formatClock12(d) {
  const pad = (n) => String(n).padStart(2, "0");
  const hours24 = d.getHours();
  const period = hours24 >= 12 ? "PM" : "AM";
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  return `${hours12}:${pad(d.getMinutes())} ${period}`;
}

export function formatLocalClockTime(value) {
  if (value == null || value === "") return "";
  const d = toLocalDate(value);
  if (d == null) return String(value);
  return formatClock12(d);
}

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Message timestamp, formatted by how OLD the message is (see the
 * chat-bubble label spec):
 *
 *   • Today            → "2:20 PM"                 (time only)
 *   • Yesterday        → "Yesterday, 2:20 PM"
 *   • Earlier this year→ "15 Jul, 2:20 PM"         (day + month)
 *   • Previous years   → "15 Jul 2025, 2:20 PM"    (day + month + year)
 *
 * All comparisons are in the viewer's LOCAL calendar, so a message sent
 * just before local midnight still reads "Today" until the day actually
 * rolls over for the viewer. ``now`` is injectable for tests.
 *
 * Returns "" for empty input and the raw string for anything unparseable
 * (same contract as ``formatLocalClockTime``).
 */
export function formatMessageTimestamp(value, now = new Date()) {
  if (value == null || value === "") return "";
  const d = toLocalDate(value);
  if (d == null) return String(value);

  const time = formatClock12(d);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, now)) return time;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday, ${time}`;

  const day = d.getDate();
  const month = SHORT_MONTHS[d.getMonth()];
  if (d.getFullYear() === now.getFullYear()) {
    return `${day} ${month}, ${time}`;
  }
  return `${day} ${month} ${d.getFullYear()}, ${time}`;
}
